import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Bell, Sparkles, MapPin, Paperclip, FileText, CheckCircle2, Star, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API, useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

// Render a string and turn [text](/jobs/id) into <Link> components
function renderRichText(text, onJobClick) {
  if (!text) return null;
  const parts = [];
  const regex = /\[([^\]]+)\]\(\/jobs\/([a-f0-9\-]+)\)/g;
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(<span key={`t${key++}`}>{text.slice(lastIdx, m.index)}</span>);
    parts.push(
      <Link
        key={`l${key++}`}
        to={`/jobs/${m[2]}`}
        onClick={onJobClick}
        className="inline-flex items-center gap-1 font-medium text-[#1A8FE3] underline underline-offset-2 hover:text-[#157AC0]"
      >
        {m[1]}
      </Link>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(<span key={`t${key++}`}>{text.slice(lastIdx)}</span>);
  return parts;
}

const AVATAR_URL = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=120&h=120&fit=crop&crop=face";

export default function ChatbotWidget() {
  const { t, lang } = useI18n();
  const { setSession, authHeader } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showAlertCta, setShowAlertCta] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [unread, setUnread] = useState(false);
  const [pendingCv, setPendingCv] = useState(null); // { file, review }
  const [credForm, setCredForm] = useState({ name: "", email: "", phone: "", password: "", title: "", company: "" });
  const [creatingAccount, setCreatingAccount] = useState(false);
  const fileInputRef = useRef(null);
  const cvFileRef = useRef(null); // hold file across re-renders for resilience
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        { role: "assistant", text: lang === "fr"
          ? "👋 Bonjour, moi c'est Maya, votre coach emploi Motravay. Je serais ravie de jeter un coup d'œil rapide à votre CV pour voir quels postes vous correspondent le mieux — souhaitez-vous le télécharger ici ?"
          : "👋 Hi, I'm Maya, your Motravay job coach. I'd love to take a quick look at your CV to see which roles fit you best — would you like to upload it here?",
          quick: lang === "fr"
            ? ["Dernières offres", "Mon CV est-il bon ?", "Aide-moi à trouver un emploi"]
            : ["Latest jobs", "Check if my CV is good", "Help me find a job"],
        },
      ]);
    }
    // eslint-disable-next-line
  }, [open, lang]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, loading, pendingCv]);

  const HUMAN_DELAY = () => 2500 + Math.floor(Math.random() * 1000); // 2.5-3.5s
  const wait = (ms) => new Promise(res => setTimeout(res, ms));

  const isCvQuickReply = (msg) => {
    const lower = msg.toLowerCase();
    return (
      lower.includes("évaluer") ||
      lower.includes("evaluate my cv") ||
      lower.includes("check if my cv") ||
      lower.includes("mon cv est-il bon") ||
      (lower.includes("cv") && lower.length < 30)
    );
  };

  const sendMessage = async (overrideText) => {
    const msg = (overrideText ?? input).trim();
    if (!msg || loading) return;
    setMessages(m => [...m, { role: "user", text: msg }]);
    setInput("");
    setShowAlertCta(false);

    // Intercept "evaluate my CV" quick reply → trigger file picker
    if (overrideText && isCvQuickReply(overrideText)) {
      setLoading(true);
      await wait(HUMAN_DELAY());
      setLoading(false);
      setMessages(m => [...m, { role: "assistant", text: lang === "fr"
        ? "Parfait ! Téléchargez votre CV ci-dessous (PDF / DOC / DOCX) et notre système vous donne une évaluation + des recommandations en quelques secondes."
        : "Great! Upload your CV below (PDF / DOC / DOCX) and our system will give you an instant evaluation and recommendations.",
        action: "request_cv",
      }]);
      return;
    }

    setLoading(true);
    try {
      const [r] = await Promise.all([
        axios.post(`${API}/chat`, { message: msg, session_id: sessionId, language: lang }, { headers: authHeader || {} }),
        wait(HUMAN_DELAY()),
      ]);
      setSessionId(r.data.session_id);
      const noMatches = !!(r.data.no_jobs || r.data.no_matches);
      // Split Maya's reply into WhatsApp-style bubbles (delimited by <<<NEXT>>>).
      const raw = (r.data.reply || "").replace(/<<<\s*NEXT\s*>>>/gi, "<<<NEXT>>>");
      const parts = raw.split("<<<NEXT>>>").map(s => s.trim()).filter(Boolean);
      const safeParts = parts.length ? parts : [r.data.reply || ""];
      // First bubble appears immediately; subsequent bubbles after a short typing pause to feel human.
      const lastIdx = safeParts.length - 1;
      for (let i = 0; i < safeParts.length; i++) {
        const isLast = i === lastIdx;
        if (i > 0) {
          setLoading(true);
          await wait(700 + Math.floor(Math.random() * 600)); // 0.7–1.3s between bubbles
          setLoading(false);
        }
        setMessages(m => [...m, {
          role: "assistant",
          text: safeParts[i],
          jobs: isLast ? (r.data.jobs || []) : [],
          action: isLast && noMatches ? "request_cv" : undefined,
        }]);
      }
      if (r.data.no_jobs) setShowAlertCta(true);
      if (!open) setUnread(true);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", text: lang === "fr" ? "Désolée, j'ai un petit souci pour le moment. Réessayez dans un instant." : "Sorry, I'm having a hiccup. Please try again in a moment." }]);
    } finally { setLoading(false); }
  };

  const onCvSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setMessages(m => [...m, { role: "user", text: `📎 ${file.name}` }]);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("create_account", "false");
      fd.append("language", lang);
      const [r] = await Promise.all([
        axios.post(`${API}/chat/cv-review`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
        wait(HUMAN_DELAY()),
      ]);
      const p = r.data?.profile || {};
      // Coerce all profile fields to strings — backend may return arrays for some fields
      const toStr = (v) => Array.isArray(v) ? v.join(", ") : (v == null ? "" : String(v));
      const safeProfile = {
        name: toStr(p.name),
        email: toStr(p.email),
        phone: toStr(p.phone),
        title: toStr(p.title),
        work_history: toStr(p.work_history),
      };
      // Best-effort extraction of latest employer from work_history (e.g. "Acme Inc — Senior Engineer — 2022-Now")
      const wh = p.work_history;
      const whString = Array.isArray(wh) ? wh.join("\n") : (typeof wh === "string" ? wh : "");
      const firstWorkLine = whString.split(/\r?\n/).map(s => s.trim()).filter(Boolean)[0] || "";
      const inferredCompany = firstWorkLine.split(/[—–\-|·]/)[0]?.trim() || "";
      cvFileRef.current = file; // ← retain across re-renders
      setPendingCv({ file, review: r.data });
      setCredForm({
        name: safeProfile.name,
        email: safeProfile.email,
        phone: safeProfile.phone,
        password: "",
        title: safeProfile.title,
        company: inferredCompany,
      });
      // 1) Review bubble
      setMessages(m => [...m, { role: "assistant", review: r.data }]);
      // 2) Benefits / soft pitch as a natural follow-up
      await wait(900);
      setMessages(m => [...m, { role: "assistant", text: lang === "fr"
        ? "Votre parcours est vraiment intéressant. En créant un compte rapidement, vous pourrez postuler facilement à nos offres et recevoir des recommandations d'emploi personnalisées basées sur votre profil. On commence ?"
        : "Your background is really strong. By quickly creating an account you can apply easily to our openings and get tailored job recommendations based on your profile. Shall we set that up?" }]);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", text: lang === "fr"
        ? "Je n'ai pas pu lire ce CV. Essayez un PDF ou un DOCX clair."
        : "I couldn't read this CV. Try a clearer PDF or DOCX." }]);
    } finally { setLoading(false); }
  };

  const createAccountFromCv = async (e) => {
    e?.preventDefault?.();
    if (!pendingCv?.file) return;
    if (!credForm.email) {
      toast.error(lang === "fr" ? "Email requis pour créer un compte." : "Email is required to create an account.");
      return;
    }
    if (!credForm.password || credForm.password.length < 6) {
      toast.error(lang === "fr" ? "Mot de passe : 6 caractères minimum." : "Password: at least 6 characters.");
      return;
    }
    setCreatingAccount(true);
    try {
      const cvFile = pendingCv?.file || cvFileRef.current;
      if (!cvFile) {
        toast.error(lang === "fr" ? "Veuillez re-télécharger votre CV." : "Please re-upload your CV.");
        setCreatingAccount(false);
        return;
      }
      // 1) Register
      const reg = await axios.post(`${API}/auth/register/candidate`, {
        name: credForm.name || "Candidate",
        email: credForm.email || undefined,
        phone: credForm.phone || undefined,
        password: credForm.password,
      });
      const token = reg.data.token;
      const user = reg.data.user;
      // 2) Upload CV using auth
      const fd = new FormData();
      fd.append("file", cvFile);
      await axios.post(`${API}/candidate/cv`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      // 2b) Save title/company from CV onto the profile (best-effort)
      const profilePatch = {};
      if (credForm.title) profilePatch.title = credForm.title;
      if (credForm.company) profilePatch.work_history = `${credForm.company}${credForm.title ? ` — ${credForm.title}` : ""}`;
      if (Object.keys(profilePatch).length > 0) {
        try {
          await axios.patch(`${API}/candidate/profile`, profilePatch, { headers: { Authorization: `Bearer ${token}` } });
        } catch (_) { /* non-blocking */ }
      }
      // 3) Persist session
      setSession(token, user);
      toast.success(lang === "fr" ? "Compte créé ! Bienvenue sur Motravay." : "Account created! Welcome to Motravay.");
      setMessages(m => [...m, { role: "assistant", text: lang === "fr"
        ? `✅ Compte créé. Vous êtes connecté(e), ${user.name}. Je vous redirige vers votre espace candidat.`
        : `✅ Account created. You're signed in, ${user.name}. Redirecting to your candidate dashboard.` }]);
      setPendingCv(null);
      cvFileRef.current = null;
      setTimeout(() => { setOpen(false); navigate("/candidate"); }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || (lang === "fr" ? "Création du compte impossible." : "Account creation failed.");
      toast.error(detail);
    } finally { setCreatingAccount(false); }
  };

  const subscribe = async () => {
    if (!alertEmail) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/alerts/subscribe`, { email: alertEmail });
      toast.success(t("jobs.subscribeSuccess"));
      setAlertEmail(""); setShowAlertCta(false);
    } catch (e) { toast.error("Failed"); }
    setSubscribing(false);
  };

  const onJobLinkClick = () => { setOpen(false); };

  return (
    <>
      {!open && (
        <>
          <button
            data-testid="chatbot-toggle"
            onClick={() => { setOpen(true); setUnread(false); }}
            aria-label="Open job assistant chat"
            className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 pl-2 pr-4 h-14 rounded-full bg-white border border-slate-200 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
          >
            <span className="relative h-10 w-10 rounded-full overflow-hidden ring-2 ring-[#1A8FE3]">
              <img src={AVATAR_URL} alt="Maya, job assistant" className="h-full w-full object-cover" />
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
            </span>
            <span className="text-left">
              <span className="block text-xs text-slate-500 leading-none">{lang === "fr" ? "Assistante" : "Job Coach"}</span>
              <span className="block text-sm font-semibold text-slate-900 leading-tight">{lang === "fr" ? "Maya · en ligne" : "Maya · online"}</span>
            </span>
            {unread && <span className="ml-1 h-2 w-2 rounded-full bg-rose-500 animate-pulse" />}
          </button>
        </>
      )}

      {open && (
        <div data-testid="chatbot-panel" className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[420px] h-[640px] max-h-[88vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-black via-slate-900 to-black text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="relative h-10 w-10 rounded-full overflow-hidden ring-2 ring-[#1A8FE3]">
                <img src={AVATAR_URL} alt="Maya" className="h-full w-full object-cover" />
              </span>
              <div>
                <div className="font-display text-base font-semibold leading-tight">Maya</div>
                <div className="text-[11px] text-emerald-300 inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {lang === "fr" ? "Assistante emploi · en ligne" : "Job Coach · online"}
                </div>
              </div>
            </div>
            <button data-testid="chatbot-close" onClick={() => setOpen(false)} aria-label="Close chat" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                {/* CV review bubble */}
                {m.review ? (
                  <CvReviewBubble review={m.review} lang={lang} />
                ) : (
                  <div className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role === "assistant" && (
                      <img src={AVATAR_URL} alt="Maya" className="h-7 w-7 rounded-full object-cover shrink-0" />
                    )}
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#1A8FE3] text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"}`}>
                      {m.role === "assistant" ? renderRichText(m.text, onJobLinkClick) : m.text}
                    </div>
                  </div>
                )}

                {/* Inline CV upload action */}
                {m.role === "assistant" && m.action === "request_cv" && (
                  <div className="pl-9">
                    <button
                      data-testid="chatbot-cv-upload"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 bg-white border border-dashed border-[#1A8FE3] text-[#1A8FE3] hover:bg-[#1A8FE3]/5 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors"
                    >
                      <Paperclip className="h-4 w-4" />
                      {lang === "fr" ? "Choisir mon CV (PDF / DOCX)" : "Choose my CV (PDF / DOCX)"}
                    </button>
                  </div>
                )}

                {m.role === "assistant" && m.quick && (
                  <div className="flex flex-wrap gap-2 pl-9">
                    {m.quick.map((q) => (
                      <button key={q} onClick={() => sendMessage(q)} data-testid={`chatbot-quick-${q}`} className="text-xs bg-white border border-slate-200 hover:border-[#1A8FE3] hover:text-[#1A8FE3] rounded-full px-3 py-1.5 transition-colors">
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                {m.role === "assistant" && m.jobs && m.jobs.length > 0 && (
                  <div className="pl-9 space-y-2">
                    {m.jobs.map(j => (
                      <Link
                        key={j.id}
                        to={`/jobs/${j.id}`}
                        onClick={onJobLinkClick}
                        data-testid={`chatbot-job-card-${j.id}`}
                        className="block bg-white border border-slate-200 hover:border-[#1A8FE3] rounded-xl p-3 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 group-hover:text-[#1A8FE3] truncate">{j.title}</div>
                            <div className="text-xs text-slate-500 truncate">{j.company} · <MapPin className="inline h-3 w-3 -mt-0.5" /> {j.location}</div>
                          </div>
                          <Sparkles className="h-4 w-4 text-[#1A8FE3] shrink-0" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Account-creation card after a CV is parsed */}
            {pendingCv && (
              <div className="pl-9" data-testid="chatbot-cv-create-account">
                <form onSubmit={createAccountFromCv} className="bg-white border border-[#1A8FE3]/40 rounded-2xl p-3.5 space-y-2.5 shadow-sm">
                  <div className="rounded-lg bg-[#1A8FE3]/8 border border-[#1A8FE3]/15 px-2.5 py-2" data-testid="chatbot-value-prop">
                    <p className="text-[11px] leading-snug text-slate-700">
                      {lang === "fr"
                        ? "Créer un compte vous permet de postuler facilement à plusieurs offres et de recevoir des recommandations d'emploi intelligentes basées sur votre profil."
                        : "Creating an account enables you to apply easily to multiple roles and get smart job recommendations based on your profile."}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                    <p className="text-xs text-slate-700">
                      {lang === "fr"
                        ? "J'ai pré-rempli vos informations à partir du CV — choisissez juste un mot de passe."
                        : "I pre-filled your info from the CV — just pick a password to finish."}
                    </p>
                  </div>
                  <Input
                    data-testid="chatbot-cv-name"
                    placeholder={lang === "fr" ? "Nom complet" : "Full name"}
                    value={credForm.name}
                    onChange={(e) => setCredForm({ ...credForm, name: e.target.value })}
                    className="h-9 text-sm"
                  />
                  <Input
                    data-testid="chatbot-cv-email"
                    type="email"
                    placeholder="you@email.com"
                    value={credForm.email}
                    onChange={(e) => setCredForm({ ...credForm, email: e.target.value })}
                    className="h-9 text-sm"
                  />
                  <Input
                    data-testid="chatbot-cv-phone"
                    placeholder={lang === "fr" ? "Téléphone (optionnel)" : "Phone (optional)"}
                    value={credForm.phone}
                    onChange={(e) => setCredForm({ ...credForm, phone: e.target.value })}
                    className="h-9 text-sm"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      data-testid="chatbot-cv-title"
                      placeholder={lang === "fr" ? "Dernier poste" : "Latest job title"}
                      value={credForm.title}
                      onChange={(e) => setCredForm({ ...credForm, title: e.target.value })}
                      className="h-9 text-sm"
                    />
                    <Input
                      data-testid="chatbot-cv-company"
                      placeholder={lang === "fr" ? "Entreprise actuelle" : "Current company"}
                      value={credForm.company}
                      onChange={(e) => setCredForm({ ...credForm, company: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <Input
                    data-testid="chatbot-cv-password"
                    type="password"
                    placeholder={lang === "fr" ? "Mot de passe (6+ caractères)" : "Password (6+ chars)"}
                    value={credForm.password}
                    onChange={(e) => setCredForm({ ...credForm, password: e.target.value })}
                    className="h-9 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      data-testid="chatbot-cv-create-submit"
                      disabled={creatingAccount}
                      className="flex-1 bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full text-xs h-9"
                    >
                      {creatingAccount
                        ? (lang === "fr" ? "Création…" : "Creating…")
                        : (lang === "fr" ? "Créer mon compte" : "Create my account")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setPendingCv(null); cvFileRef.current = null; }}
                      className="rounded-full text-xs h-9"
                    >
                      {lang === "fr" ? "Annuler" : "Cancel"}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {loading && (
              <div className="flex items-end gap-2 justify-start">
                <img src={AVATAR_URL} alt="Maya" className="h-7 w-7 rounded-full object-cover shrink-0" />
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3 inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}

            {showAlertCta && (
              <div data-testid="chatbot-alert-cta" className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2 mt-2">
                <p className="text-xs text-amber-900">{t("chat.noJobsAction")}</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => { navigate("/jobs"); setOpen(false); }} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A8FE3] hover:underline">{t("chat.browseJobs")}</button>
                </div>
                <div className="flex gap-2 items-center">
                  <Input value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} placeholder="you@email.com" className="h-9 text-xs" data-testid="chatbot-alert-email" />
                  <Button onClick={subscribe} disabled={subscribing || !alertEmail} size="sm" data-testid="chatbot-alert-subscribe" className="h-9 bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full whitespace-nowrap"><Bell className="h-3 w-3 mr-1" />{t("chat.subscribe")}</Button>
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="p-3 border-t border-slate-200 bg-white flex gap-2 items-center">
            <button
              type="button"
              data-testid="chatbot-attach-cv"
              onClick={() => fileInputRef.current?.click()}
              aria-label={lang === "fr" ? "Joindre un CV" : "Attach a CV"}
              className="h-10 w-10 rounded-full border border-slate-200 text-slate-500 hover:text-[#1A8FE3] hover:border-[#1A8FE3] inline-flex items-center justify-center transition-colors shrink-0"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              className="hidden"
              onChange={onCvSelected}
            />
            <Input data-testid="chatbot-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("chat.placeholder")} className="flex-1" />
            <Button type="submit" disabled={loading || !input.trim()} data-testid="chatbot-send" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full px-4"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}

function CvReviewBubble({ review, lang }) {
  const score = review.cv_score || 0;
  const color = score >= 75 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-rose-600";
  return (
    <div className="flex items-start gap-2" data-testid="chatbot-cv-review">
      <img src={AVATAR_URL} alt="Maya" className="h-7 w-7 rounded-full object-cover shrink-0 mt-1" />
      <div className="max-w-[88%] bg-white border border-slate-200 rounded-2xl rounded-bl-sm shadow-sm p-3.5 text-sm">
        <div className="flex items-center gap-3 pb-2 border-b border-slate-100 mb-2.5">
          <FileText className="h-4 w-4 text-[#1A8FE3]" />
          <span className="font-semibold text-slate-900">{lang === "fr" ? "Évaluation de votre CV" : "Your CV review"}</span>
          <span className={`ml-auto inline-flex items-center gap-1 font-display font-bold text-lg ${color}`}>
            <Star className="h-4 w-4" />{score}/100
          </span>
        </div>
        {review.summary && <p className="text-slate-700 mb-2.5 leading-relaxed">{review.summary}</p>}
        {review.strengths?.length > 0 && (
          <div className="mb-2.5">
            <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">{lang === "fr" ? "Points forts" : "Strengths"}</p>
            <ul className="space-y-1">
              {review.strengths.map((s, i) => (
                <li key={i} className="flex gap-2 text-xs text-slate-700"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />{s}</li>
              ))}
            </ul>
          </div>
        )}
        {review.improvements?.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold mb-1">{lang === "fr" ? "Améliorations" : "Improvements"}</p>
            <ul className="space-y-1">
              {review.improvements.map((s, i) => (
                <li key={i} className="flex gap-2 text-xs text-slate-700"><AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />{s}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
